/**
 * TEAM_APPS registry — issue #753, Phase 0 (dark).
 *
 * One declaration per app kind, keyed by `teams.team_kind`. The shell
 * reads this registry to render the right home + nav for a team's kind.
 *
 * Design ref: `docs/design/task-based-mode-exploration-2026-05-22.md` §A2.
 * Wireframe ref: B7.2 — the tasks team's nav is Map · Members · Settings.
 *
 * Adding a third app later = add a key here + a frontend module; no shell
 * re-plumbing needed (open/closed principle per §A2).
 *
 * **Dark gate discipline:** a 'tasks' team only appears if the server-side
 * `TARII_TASK_MODE` flag is on (no existing team ever has `team_kind='tasks'`
 * in prod while the gate is off). The SPA doesn't need its own env gate —
 * the WorkspaceRow from the API simply never carries `team_kind='tasks'`
 * for any real team until the gate is flipped. Tests mock 'tasks' teams to
 * exercise the branch.
 *
 * Brand discipline:
 *  - Worker-friendly + trustable copy; no tier language; no individual-
 *    performance framing.
 *  - Voice is the product ("we"/neutral).
 *
 * Isolation rule (matches the backend `taskmap ⊥ scheduler` contract):
 *  - The tasks module must not import scheduling/cycle/solver components.
 *  - The scheduling module must not import taskmap components.
 *  - This registry file itself is neutral (no cross-module imports).
 */

export type TeamKind = "scheduling" | "tasks";

export interface NavItem {
  /** Nav link label. */
  label: string;
  /** Route path — may contain `:teamId` placeholder. */
  to: string;
  /** Whether this is the focal (most-frequented) link for this app. */
  focal: boolean;
  /** NavLink `end` prop — true = exact match for active highlighting. */
  end: boolean;
}

export interface TeamAppDef {
  /** Human-readable label for the app kind. */
  label: string;
  /** Icon name from the canonical set (resolved in UI by consumers). */
  iconKey: "calendar" | "map";
  /**
   * Home route for this app kind. Contains `:teamId` placeholder;
   * callers replace it with the actual team id.
   */
  homeRoute: string;
  /**
   * Nav items to render in the AppShell right-side strip for this kind.
   * Returned as a factory so callers can inject the resolved teamId.
   */
  navItems: (teamId: string) => NavItem[];
}

/** TEAM_APPS registry — the single source of truth for per-kind shell config. */
export const TEAM_APPS: Record<TeamKind, TeamAppDef> = {
  scheduling: {
    label: "Rostering",
    iconKey: "calendar",
    homeRoute: "/teams/:teamId",
    navItems: (teamId: string): NavItem[] => [
      // #2030 — "Team" (was "Timeline"); #2048 — "Home", matching the lead strip.
      { label: "Home", to: `/teams/${teamId}`, focal: true, end: false },
      { label: "Members", to: `/teams/${teamId}?tab=members`, focal: false, end: false },
      { label: "Setup", to: `/teams/${teamId}/setup/sessions`, focal: false, end: false },
    ],
  },

  tasks: {
    label: "Map",
    iconKey: "map",
    homeRoute: "/teams/:teamId/map",
    navItems: (teamId: string): NavItem[] => [
      // #1043 (IA shell, founder 2026-05-26) — the shared Harbour topbar's
      // tasks-team nav is the THREE app-level destinations: Tasks (the Map
      // workspace, focal), Team (members), Settings. The within-app Home ·
      // Taskmap secondary nav lives in `<MapNav/>` (editorial Map chrome), NOT
      // here. `label` stays "Map" (app identity, a separate question). Never
      // Cycles/Stations/Solve (those are the scheduling app).
      { label: "Tasks", to: `/teams/${teamId}/map`, focal: true, end: false },
      { label: "Team", to: `/teams/${teamId}/map/members`, focal: false, end: false },
      { label: "Settings", to: `/teams/${teamId}/map/settings`, focal: false, end: false },
    ],
  },
} as const;

/**
 * Resolve the home route for a given team kind + team id.
 * Pure function; used by WorkspaceSwitcher to derive the navigation destination.
 */
export function resolveTeamHomeRoute(kind: TeamKind, teamId: string): string {
  return TEAM_APPS[kind].homeRoute.replace(":teamId", teamId);
}
